import { Suspense, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";
import { useSubscriptionSync } from "@/hooks/useSubscriptionSync";
import useAuth from "@/hooks/useAuth";
import { useFeatureFlagsContext } from "@/contexts/FeatureFlagsContext";

interface SubscriptionProtectedProps {
  children: React.ReactNode;
}

const PAYMENT_VERIFICATION_TIMEOUT_MS = 30_000;

export default function SubscriptionProtected({
  children,
}: SubscriptionProtectedProps) {
  const { loading: flagLoading } = useFeatureFlagsContext();
  const { hasAccess } = useSubscriptionAccess();
  const { refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const cameFromCheckout = useRef(searchParams.get("session_id") !== null);

  // Muestra loader solo si el usuario aún no tiene acceso al llegar desde checkout.
  // Los usuarios con acceso previo (ej. legacy en grace period) no necesitan loader —
  // el sync de Realtime actualiza el banner silenciosamente en background.
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(
    cameFromCheckout.current && !hasAccess,
  );

  // Limpiar session_id de la URL al montar
  useEffect(() => {
    if (cameFromCheckout.current) {
      const cleaned = new URLSearchParams(searchParams);
      cleaned.delete("session_id");
      setSearchParams(cleaned, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh inmediato al volver de checkout — cubre el caso donde el webhook
  // ya procesó antes de que el componente montara.
  useEffect(() => {
    if (cameFromCheckout.current) {
      refreshUser();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling de respaldo mientras se verifica el pago.
  // Cubre la condición de carrera donde el webhook procesa durante el tiempo
  // que tarda el canal Realtime en establecerse (1-3s de handshake WebSocket).
  useEffect(() => {
    if (!cameFromCheckout.current || !isVerifyingPayment) return;
    const interval = setInterval(() => {
      refreshUser();
    }, 3000);
    return () => clearInterval(interval);
  }, [isVerifyingPayment, refreshUser]);

  // Suscripción Realtime: cuando el webhook actualiza `subscriptions`,
  // el evento dispara refreshUser() y el contexto se sincroniza automáticamente.
  useSubscriptionSync();

  // Ocultar loader cuando se confirma el acceso
  useEffect(() => {
    if (hasAccess) setIsVerifyingPayment(false);
  }, [hasAccess]);

  // Safety timeout: ocultar loader si el evento de Realtime nunca llega
  useEffect(() => {
    if (!isVerifyingPayment) return;
    const timeout = setTimeout(
      () => setIsVerifyingPayment(false),
      PAYMENT_VERIFICATION_TIMEOUT_MS,
    );
    return () => clearTimeout(timeout);
  }, [isVerifyingPayment]);

  if (flagLoading || isVerifyingPayment) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        {isVerifyingPayment && (
          <>
            <p className="text-gray-600">Verificando tu pago...</p>
            <p className="text-sm text-gray-400">
              Esto puede tomar unos segundos
            </p>
          </>
        )}
      </div>
    );
  }

  const contentSpinner = (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );

  return <Suspense fallback={contentSpinner}>{children}</Suspense>;
}
